'use client'

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'

import { getUser, toggleUserArticle, toggleUserBill } from '@/app/actions/user'

import type { User } from '@/payload-types'

function getTrackedBillIds(user: User | null): number[] {
  if (!user?.Bills?.bills) return []
  return user.Bills.bills.map((b) => (typeof b === 'number' ? b : b.id))
}

function getSavedArticleIds(user: User | null): number[] {
  if (!user?.Articles?.articles) return []
  return user.Articles.articles.map((a) => (typeof a === 'number' ? a : a.id))
}

interface UserTrackingContextType {
  trackedBillIds: number[] | undefined
  onToggleTrack: ((billId: number) => Promise<void>) | undefined
  savedArticleIds: number[] | undefined
  onToggleSaveArticle: ((articleId: number) => Promise<void>) | undefined
  /** Re-fetch user so tracked state stays in sync (e.g. after toggling on Dashboard). */
  user: User | null | 'pending'
}

const UserTrackingContext = createContext<UserTrackingContextType | undefined>(undefined)

export const useUserTracking = (): UserTrackingContextType | undefined => {
  return useContext(UserTrackingContext)
}

export const UserTrackingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null | 'pending'>('pending')

  useEffect(() => {
    const fetchUser = async () => {
      const data = await getUser()
      if (data.status === 'error') {
        setUser(null)
        return
      }
      setUser(data.data)
    }
    fetchUser()
  }, [])

  const onToggleTrack = useCallback(async (billId: number) => {
    const data = await toggleUserBill(billId)
    if (data.status === 'error') {
      setUser(null)
      return
    }
    setUser(data.data)
  }, [])

  const onToggleSaveArticle = useCallback(async (articleId: number) => {
    const data = await toggleUserArticle(articleId)
    if (data.status === 'error') {
      setUser(null)
      return
    }
    setUser(data.data)
  }, [])

  const value: UserTrackingContextType =
    user === 'pending' || user === null
      ? {
          trackedBillIds: undefined,
          onToggleTrack: undefined,
          savedArticleIds: undefined,
          onToggleSaveArticle: undefined,
          user,
        }
      : {
          trackedBillIds: getTrackedBillIds(user as User),
          onToggleTrack,
          savedArticleIds: getSavedArticleIds(user as User),
          onToggleSaveArticle,
          user,
        }

  return <UserTrackingContext.Provider value={value}>{children}</UserTrackingContext.Provider>
}
